import type { SortDescriptor } from "react-stately";

import { useNavigate } from "@tanstack/react-router";
import { Copy, Trash2 } from "lucide-react";
import { memo, useCallback, useEffect, useMemo, useState } from "react";

import type { BulkOperation } from "@/components/bulk-operations-toolbar";

import { toast } from "@/components/toast/toast-queue.ts";
import { handleDuplicatePastRequest } from "@/features/request/duplicate-past-request-modal/useDuplicatePastRequestForm";
import { draftsTableConfig } from "@/features/requests/config/tables";
import { useListProjectsMinimal } from "@/hooks/useListProjectsMinimal.ts";
import { useUser } from "@/hooks/useUser";

import type { RequestFilterType } from "../../../types/filters";

import {
  useBulkDeleteRequests,
  useBulkDuplicateRequests,
  useDraftRequests,
} from "../../../hooks";
import { useResetFilterOnEmptySearch } from "../../../hooks/shared";
import { BulkDeleteModal } from "../../modals/BulkDeleteModal";
import { BulkDuplicateModal } from "../../modals/BulkDuplicateModal";
import { ConfigurableTable } from "../../tables";

const getTableRowId = (requestId: number | string): string =>
  `${draftsTableConfig.id}-${requestId}`;

const getRequestIdFromTableRowId = (tableRowId: string): string =>
  tableRowId.replace(`${draftsTableConfig.id}-`, "");

interface DraftsTabProps {
  onFilterChange?: (filter: RequestFilterType) => void;
  searchValue?: string;
  selectedFilter?: RequestFilterType;
}

function DraftsTabComponent({
  onFilterChange,
  searchValue,
  selectedFilter,
}: DraftsTabProps) {
  const { data: userData } = useUser();
  const { data } = useListProjectsMinimal();
  const projects = data?.projects;
  const navigate = useNavigate();

  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: draftsTableConfig.defaultSort?.column || "createdAt",
    direction:
      draftsTableConfig.defaultSort?.direction === "desc"
        ? "descending"
        : "ascending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Bulk operations state
  const [selectedItems, setSelectedItems] = useState<Set<number | string>>(
    new Set(),
  );

  // Modal states
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const {
    data: requestsData,
    isFetching,
    isLoading,
    isSuccess,
    refetch,
  } = useDraftRequests({
    filter: selectedFilter,
    limit: itemsPerPage,
    page: currentPage,
    search: searchValue,
    sortDescriptor,
  });

  useResetFilterOnEmptySearch({
    // Never the broadest scope ("All" tab is), so always escalate.
    isAlreadyBroadest: false,
    isReady: isSuccess && !isFetching,
    onSwitchToAll: () => onFilterChange?.("all"),
    searchTerm: searchValue,
    totalMatches: requestsData?.total ?? 0,
  });

  const bulkDeleteMutation = useBulkDeleteRequests({
    onError: (_error) => {
      toast.add({
        description: "Failed to delete requests. Please try again.",
        timeout: 3000,
        title: "Delete Failed",
        type: "error",
      });
    },
    onSuccess: () => {
      setSelectedItems(new Set());
      setIsDeleteModalOpen(false);
      toast.add({
        description: "Draft requests have been deleted successfully.",
        timeout: 3000,
        title: "Requests Deleted",
        type: "success",
      });
      refetch();
    },
  });

  const bulkDuplicateMutation = useBulkDuplicateRequests({
    onError: (_error) => {
      toast.add({
        description: "Failed to duplicate requests. Please try again.",
        timeout: 3000,
        title: "Duplicate Failed",
        type: "error",
      });
    },
    onSuccess: (data) => {
      setSelectedItems(new Set());
      setIsDuplicateModalOpen(false);
      const { failed, successful } = data.summary;

      if (failed > 0) {
        toast.add({
          description: `${successful} requests duplicated, ${failed} failed.`,
          timeout: 4000,
          title: "Partially Completed",
          type: "warning",
        });
      } else {
        toast.add({
          description: `${successful} request${successful === 1 ? "" : "s"} duplicated successfully.`,
          timeout: 3000,
          title: "Requests Duplicated",
          type: "success",
        });
      }

      if (data.results.length === 1) {
        const newRequestId = data.results[0]?.duplicatedRequest?.id;

        if (typeof newRequestId === "number") {
          navigate({
            params: { requestId: newRequestId },
            to: "/requests/$requestId/edit",
          });

          return;
        }
      }

      refetch();
    },
  });

  const draftRequests = requestsData?.requests || [];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Bulk operations handlers
  const handleBulkDelete = async () => {
    setIsDeleteModalOpen(true);
  };

  const confirmBulkDelete = () => {
    const selectedIds = [...selectedItems]
      .map((id) => getRequestIdFromTableRowId(String(id)))
      .map(Number)
      .filter((id) => !Number.isNaN(id) && id > 0);

    if (selectedIds.length === 0) {
      toast.add({
        description: "No valid requests selected for deletion.",
        timeout: 3000,
        title: "Invalid Selection",
        type: "error",
      });

      return;
    }

    bulkDeleteMutation.mutate(selectedIds);
  };

  const handleBulkDuplicate = async () => {
    setIsDuplicateModalOpen(true);
  };

  const confirmBulkDuplicate = (options: {
    includeAttachments: boolean;
    includeCollaborators: boolean;
  }) => {
    const selectedIds = [...selectedItems]
      .map((id) => getRequestIdFromTableRowId(String(id)))
      .map(Number);

    const singleDraft =
      selectedIds.length === 1
        ? draftRequests.find((request) => request.id === selectedIds[0])
        : undefined;

    if (singleDraft) {
      setIsDuplicateModalOpen(false);
      handleDuplicatePastRequest(
        {
          ...options,
          includeLeadershipNotes: false,
          numberOfDuplicates: 1,
          requestId: singleDraft.id,
          requestType: singleDraft.requestType,
        },
        navigate,
      );

      return;
    }

    bulkDuplicateMutation.mutate({
      duplicateOptions: { ...options, includeInternalNotes: true },
      requestIds: selectedIds,
    });
  };

  const handleSelectAll = useCallback(() => {
    const allCurrentPageIds = draftRequests.map((request) =>
      getTableRowId(request.id),
    );
    const areAllSelected = allCurrentPageIds.every((id) =>
      selectedItems.has(id),
    );

    if (areAllSelected) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(allCurrentPageIds));
    }
  }, [draftRequests, selectedItems]);
  const handleClearSelection = () => {
    setSelectedItems(new Set());
  };

  const handleSelectionChange = useCallback(
    (newSelection: Set<number | string>) => {
      // Convert from table row IDs to our internal format (should already be in table format)
      const validSelection = new Set([...newSelection].map(String));

      setSelectedItems(new Set(validSelection));
    },
    [],
  );

  const handleTriggerBulkMode = (requestId: number) => {
    setSelectedItems(new Set([getTableRowId(requestId)]));
  };

  // Direct action handlers for single-item operations
  const handleDirectDelete = (requestId: number) => {
    // Select the specific item and immediately show delete modal
    setSelectedItems(new Set([getTableRowId(requestId)]));
    setIsDeleteModalOpen(true);
  };

  const handleDirectDuplicate = (requestId: number) => {
    // Select the specific item and immediately show duplicate modal
    setSelectedItems(new Set([getTableRowId(requestId)]));
    setIsDuplicateModalOpen(true);
  };

  // Select top N items (1-5) for quick bulk operations
  const handleSelectTopN = useCallback(
    (count: number) => {
      const availableCount = Math.min(count, draftRequests.length);

      if (availableCount === 0) {
        setSelectedItems(new Set());

        return;
      }

      const topItems = draftRequests
        .slice(0, availableCount)
        .map((item) => getTableRowId(item.id));

      // Force new Set instance and trigger re-render
      setSelectedItems(new Set(topItems));
    },
    [draftRequests],
  );

  // Permission logic for bulk operations
  const bulkOperationsPermissions = useMemo(() => {
    const userRole = userData?.user?.roleName;
    const userProjects = projects || [];

    // No permissions if user data is not available
    if (!userRole) {
      return {
        canChangeAccount: false,
        canDelete: false,
        canDuplicate: false,
      };
    }

    // Super Admin and Team Leader permissions
    if (userRole === "super-admin" || userRole === "team-leader") {
      return {
        canChangeAccount: true,
        canDelete: true,
        canDuplicate: true,
      };
    }

    // Customer Owner permissions
    if (userRole === "owner") {
      return {
        canChangeAccount: userProjects.length > 1, // Only if owns multiple accounts
        canDelete: true,
        canDuplicate: true,
      };
    }

    // Customer Admin permissions
    if (userRole === "admin") {
      return {
        canChangeAccount: userProjects.length > 1, // Only if belongs to multiple accounts
        canDelete: true,
        canDuplicate: true,
      };
    }

    // Collaborator permissions - can duplicate but limited other operations
    if (userRole === "collaborator") {
      return {
        canChangeAccount: false,
        canDelete: false,
        canDuplicate: true,
      };
    }

    // All other roles (designer, etc.) have no bulk operation permissions
    return {
      canChangeAccount: false,
      canDelete: false,
      canDuplicate: false,
    };
  }, [userData?.user?.roleName, projects]);

  // Add keyboard shortcuts for bulk operations - always active on Drafts tab
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const isTyping =
        target?.tagName === "INPUT" ||
        target?.tagName === "TEXTAREA" ||
        target?.contentEditable === "true";

      // Early return if typing in input
      if (isTyping) return;

      const key = event.key.toLowerCase();
      const hasModifier = event.ctrlKey || event.metaKey;

      switch (key) {
        case "escape": {
          if (selectedItems.size > 0) {
            event.preventDefault();
            handleClearSelection();
          }
          break;
        }
        case "delete":
        case "backspace": {
          if (selectedItems.size > 0 && bulkOperationsPermissions.canDelete) {
            event.preventDefault();
            handleBulkDelete();
          }
          break;
        }
        case "a": {
          if (hasModifier && draftRequests.length > 0) {
            event.preventDefault();
            handleSelectAll();
          }
          break;
        }
        case "d": {
          if (
            selectedItems.size > 0 &&
            bulkOperationsPermissions.canDuplicate
          ) {
            event.preventDefault();
            handleBulkDuplicate();
          }
          break;
        }
        default: {
          const num = Number.parseInt(key, 10);

          if (num >= 1 && num <= 5 && !hasModifier) {
            event.preventDefault();
            handleSelectTopN(num);
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [
    selectedItems.size,
    draftRequests.length,
    handleBulkDelete,
    handleBulkDuplicate,
    handleClearSelection,
    handleSelectAll,
    handleSelectTopN,
    bulkOperationsPermissions.canDelete,
    bulkOperationsPermissions.canDuplicate,
  ]);

  // Define bulk operations based on permissions
  const bulkOperations: BulkOperation[] = useMemo(() => {
    const operations: BulkOperation[] = [];

    // Add duplicate operation first if user has permission
    if (bulkOperationsPermissions.canDuplicate) {
      operations.push({
        color: "secondary",
        icon: Copy,
        id: "duplicate",
        label: "Duplicate request type",
        onClick: handleBulkDuplicate,
      });
    }

    // Add delete operation if user has permission
    if (bulkOperationsPermissions.canDelete) {
      operations.push({
        color: "destructive",
        icon: Trash2,
        id: "delete",
        label: "Delete",
        onClick: handleBulkDelete,
      });
    }

    return operations;
  }, [bulkOperationsPermissions, handleBulkDuplicate, handleBulkDelete]);

  return (
    <>
      <ConfigurableTable
        config={draftsTableConfig}
        customProps={{
          onDirectDelete: handleDirectDelete,
          onDirectDuplicate: handleDirectDuplicate,
          onTriggerBulkMode: handleTriggerBulkMode,
        }}
        data={draftRequests}
        isLoading={isLoading}
        key={`table-${[...selectedItems].join("-")}`}
        paginationProps={{
          metadata: {
            currentPage,
            itemsPerPage,
            totalItems: requestsData?.total ?? null,
            totalPages: requestsData?.pagination?.totalPages ?? null,
          },
          onItemsPerPageChange: handleItemsPerPageChange,
          onPageChange: handlePageChange,
          pageSizeOptions: [10, 25, 50, 100],
          showPageSizeSelector: true,
        }}
        selectionConfig={{
          bulkOperations,
          enabled: true,
          isLoading:
            bulkDeleteMutation.isPending || bulkDuplicateMutation.isPending,
          key: `selection-${[...selectedItems].join("-")}`,
          onClearSelection: handleClearSelection,
          onSelectAll: handleSelectAll,
          onSelectionChange: handleSelectionChange,
          selectedItems,
        }}
        setSortDescriptor={setSortDescriptor}
        sortDescriptor={sortDescriptor}
        useAdvancedSorting={true}
      />

      <BulkDeleteModal
        isLoading={bulkDeleteMutation.isPending}
        isOpen={isDeleteModalOpen}
        onConfirm={confirmBulkDelete}
        onOpenChange={setIsDeleteModalOpen}
        selectedCount={selectedItems.size}
      />

      <BulkDuplicateModal
        isLoading={bulkDuplicateMutation.isPending}
        isOpen={isDuplicateModalOpen}
        onConfirm={confirmBulkDuplicate}
        onOpenChange={setIsDuplicateModalOpen}
        selectedCount={selectedItems.size}
      />
    </>
  );
}

export const DraftsTab = memo(DraftsTabComponent);
